import React, { FC, useState, useReducer } from 'react';
import { useQuery } from 'react-apollo';
import { useParams } from 'react-router';
import { Button, Divider, Table, Message, Select } from 'semantic-ui-react';

import AdminLayout from 'layouts/AdminLayout';

import Title from 'components/Title';
import ErrorDisplay from 'components/ErrorDisplay';
import Loader from 'components/Loader';
import ValidatedInput from 'components/ValidatedInput';

import CartContext, { CartDishType } from 'contexts/CartContext';
import reducer, { initialState } from 'reducers/CartReducer';

import MenuTable from './MenuTable';
import FETCH_VENDOR from 'graphql/queries/fetchVendorForGuest.graphql';

import theme from './theme.scss';
import OrderFood from './OrderFood';
import { bool } from 'prop-types';

type VendorQueryResponse = {
  vendor: {
    id: string;
    name: string;
    image: string;
    status: string;
    foodCourt: {
      id: string;
    };
    menus: {
      id: string;
      name: string;
      dishesByCategory: {
        dishes: {
          id: string;
          name: string;
          description: string;
          price: number;
          image: string;
          isVegetarian: boolean;
        }[];
        category: {
          id: string;
          name: string;
        };
      };
    };
  };
  purse: {
    id: string;
    amount: number;
  };
  organizationDashboard: {
    id: string;
    name: string;
  }[];
};

type VendorQueryVariables = {
  vendorId: string;
};

export const cartToGraphqlDTO = (cartDish: CartDishType) => ({
  dishId: cartDish.id,
  quantity: cartDish.quantity,
});

const Vendor: FC = () => {
  const [cartState, cartDispatch] = useReducer(reducer, initialState);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhoneNumber, setGuestPhoneNumber] = useState('');
  const [guestOrganization, setGuestOrganization] = useState('');

  const [openOrderStatus, toggleOrderStatus] = useState(false);
  const [openConfirmationModal, toggleConfirmationModal] = useState(false);

  const { vendorId } = useParams();
  const { loading, error, data } = useQuery<VendorQueryResponse, VendorQueryVariables>(
    FETCH_VENDOR,
    {
      variables: {
        vendorId: vendorId ? vendorId : '',
      },
    }
  );

  const cartContext = { cartState, cartDispatch };

  if (loading) return <Loader type="screen" />;

  if (error || !data || !data.vendor) return <ErrorDisplay type="screen" />;

  const resetOrderForm = (showMessage?: boolean) => {
    setGuestEmail('');
    setGuestName('');
    setGuestPhoneNumber('');
    setGuestOrganization('');
    toggleConfirmationModal(false);
    toggleOrderStatus(showMessage || false);
    cartDispatch({ type: 'CLEAR_CART' });
  };

  const vendor = data.vendor;
  const categories = vendor.menus[0].dishesByCategory;
  const purse = {
    id: data.purse.id,
    amount: data.purse.amount,
  };
  const organizations = data.organizationDashboard;
  const dishes = cartState.dishesOnCart.map(cartDish => {
    return cartToGraphqlDTO(cartDish);
  });

  const orderRequestObject = {
    vendorId,
    name: guestName,
    email: guestEmail,
    phoneNumber: guestPhoneNumber,
    dishes,
    organizationId: guestOrganization,
  };
  return (
    <CartContext.Provider value={cartContext}>
      <AdminLayout>
        <div className={theme.vendorPage}>
          <Title name={vendor.name} />
          <Divider />
          {categories.map(c => (
            <>
              <h2>{c.category.name}</h2>
              <MenuTable key={c.id} dishes={c.dishes} vendor={vendor} />
              <br />
              <br />
            </>
          ))}
          <h2>CART</h2>
          <div className={theme.cartTable}>
            <Table unstackable compact className={theme.itemTable}>
              <Table.Header className={theme.itemHeader}>
                <Table.Row>
                  <Table.HeaderCell width="6">NAME</Table.HeaderCell>
                  <Table.HeaderCell width="5" textAlign="center">
                    QUANTITY
                  </Table.HeaderCell>
                  <Table.HeaderCell width="5" textAlign="center">
                    PRICE
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body className={theme.itemBody}>
                {cartState.dishesOnCart.map(dish => (
                  <Table.Row key={dish.id} className={theme.itemRow}>
                    <Table.Cell width="6">{dish.name}</Table.Cell>
                    <Table.Cell width="5" textAlign="center">
                      {dish.quantity}
                    </Table.Cell>
                    <Table.Cell width="5" textAlign="center">
                      ₹ {dish.price * dish.quantity}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {cartState.dishesOnCart && cartState.dishesOnCart.length > 0 ? (
                  <Table.Row key={'total'} className={theme.itemRow} warning>
                    <Table.Cell width="6">TOTAL</Table.Cell>
                    <Table.Cell width="5" textAlign="center">
                      {cartState.dishesOnCart.reduce((sum, d) => sum + d.quantity, 0)}
                    </Table.Cell>
                    <Table.Cell width="5" textAlign="center">
                      ₹ {cartState.dishesOnCart.reduce((sum, d) => sum + d.quantity * d.price, 0)}
                    </Table.Cell>
                  </Table.Row>
                ) : null}
              </Table.Body>
            </Table>
            {cartState.dishesOnCart && cartState.dishesOnCart.length > 0 ? null : (
              <div className={theme.emptyMessage}>There are no dishes added yet.</div>
            )}
          </div>
          <Divider hidden />
          {cartState.dishesOnCart && cartState.dishesOnCart.length > 0 ? (
            <>
              <h2>PAYMENT</h2>
              <p className={theme.walletDetail}>
                Amount to Pay: ₹{' '}
                {cartState.dishesOnCart.reduce((sum, d) => sum + d.quantity * d.price, 0)}
              </p>
              <>
                <Divider />
                <h2>GUEST INFORMATION</h2>
                <div className={theme.information}>
                  <div className={theme.field}>
                    <p className={theme.label}>NAME</p>
                    <ValidatedInput
                      validator="shortText"
                      required={true}
                      value={guestName}
                      onValueChange={value => setGuestName(value)}
                    />
                  </div>
                  <div className={theme.field}>
                    <p className={theme.label}>EMAIL</p>
                    <ValidatedInput
                      validator="email"
                      required={true}
                      value={guestEmail}
                      onValueChange={value => setGuestEmail(value)}
                    />
                  </div>
                  <div className={theme.field}>
                    <p className={theme.label}>PHONE NUMBER</p>
                    <ValidatedInput
                      validator="number"
                      required={true}
                      value={guestPhoneNumber}
                      onValueChange={value => setGuestPhoneNumber(value)}
                    />
                  </div>
                  <div className={theme.field}>
                    <p className={theme.label}>ORGANIZATION</p>
                    <Select
                      placeholder="Select organization"
                      onChange={(e, { value }) => {
                        // @ts-ignore
                        setGuestOrganization(value);
                      }}
                      value={guestOrganization}
                      options={organizations.map(o => ({ key: o.id, value: o.id, text: o.name }))}
                    />
                  </div>
                  {error ? (
                    <Message error header="An error occured. Please try again later." />
                  ) : null}
                </div>
              </>
              <Button
                positive
                className={theme.makePaymentButton}
                onClick={() => toggleConfirmationModal(true)}
              >
                Make Payment
              </Button>
            </>
          ) : null}
          <Divider />
          {openConfirmationModal ? (
            <div className={theme.information}>
              <OrderFood
                orderRequestObject={orderRequestObject}
                resetOrderForm={value => resetOrderForm(value)}
              />
            </div>
          ) : null}
          {openOrderStatus ? (
            <div className={theme.information}>
              <Message positive size="large">
                <Message.Header>Order placed successfully</Message.Header>
                <p>Order information sent to guest</p>
              </Message>
            </div>
          ) : null}
        </div>
      </AdminLayout>
    </CartContext.Provider>
  );
};

export default Vendor;
